import {
  EmrAddRounded,
  EmrBed,
  EmrBubbleChatIndicator,
  EmrCar,
  EmrDischarged,
  EmrEar,
  EmrNotes,
  EmrPerson,
  EmrUndo,
} from "@medstonetech/slate-icons";

import * as React from "react";

import {
  AddPatientChartModal,
  ChartNotesModal,
  ChartPicker,
  OrderChartNotesModal,
  OrderQueueItem,
  SidebarChartPatientCard,
  VitalsTimerBadge,
  determineChartRouteAndLabel,
  useChartSectionNotesCount,
  useChartSidebarSectionIndicator,
  useChartSidebarSharedSectionIndicator,
  useOrderQueue,
} from "modules/charts-shared";
import {
  AuditCommentsSectionIndicator,
  AuditCommentsSharedSectionIndicator,
} from "./audit-comments";
import {
  BackButton,
  Counter,
  Loading,
  NavigationButton,
  NavigationButtonContent,
  NavigationButtonContentProps,
  Sidebar,
  SidebarActions,
  SidebarBottom,
  SidebarContent,
  SidebarHeader,
  SidebarUserView,
  WarningDialog,
} from "shared";
import {
  Box,
  Button,
  ChakraProps,
  Divider,
  Flex,
  HStack,
  VStack,
  Icon,
  IconButton,
  Text,
  useDisclosure,
  chakra,
  useToast,
} from "@chakra-ui/react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient, QueryKey } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { AxiosResponse } from "axios";
import { AuditCommentsIndicator } from "modules/charts-shared/types/audit-comments";

import { ChartSectionHeaderMenu } from "./ChartSectionHeader";
import { DiagramCanvasModal } from "./diagrams/DiagramCanvasModal";
import { OrderFormElementType, OrderGroup } from "enums";
import { SharedChartNotesModal } from "./chart-notes/SharedChartNotesModal";
import { useDiagramsCounter } from "./diagrams/api";
import {
  useAuditChartReadOnly,
  useAuditPermissions,
  useChartTrackingContext,
  useHHERAllyContext,
  usePermissions,
} from "contexts";
import { useScrollbarWidth } from "hooks";
import {
  useMedsAndIvCounters,
  useTreatmentsCounters,
} from "modules/triage/api";
import { useSectionSuggestions } from "modules/charts-shared/hooks/useSectionSuggestions";
import { resolveSectionSuggestionCode } from "modules/charts-shared/hooks/sectionSuggestionCode";
import {
  useEncounterOrderCancellationCounters,
  useEncounterOrderCounters,
} from "modules/orders/api";
import { useEKGCounters } from "modules/provider/api";
import { SignedSectionButton } from "./summary-signed-section-button";
import { useAuditStatus, useUpdateAuditStatus } from "modules/audits/api";
import { getAuditStatusForChartType } from "modules/audits/utils";
import { formMessages, genericErrors } from "messages";
import { extractApiErrorMessage, isSameUserId } from "utils";
import { NURSE_CHARTS } from "modules/triage/constants";
import { PROVIDER_CHARTS } from "modules/provider/constants";
import { useEncounterSideInfo } from "modules/reception/api";
import { USERID_CLAIMS } from "system-constants";

type ChartsSidebarProps = {
  children?: React.ReactNode;
};

type ChartNavigationButtonNotesProps = {
  hasNotes: true;
} & (
  | {
      notesType: "general" | "shared";
    }
  | {
      notesType: "order";
      orderType: OrderGroup;
    }
);

type ChartNavigationButtonProps = {
  sectionId: string;
  sectionCode?: string;
  encounterId: string;
  to: string;
  chartCode: string;
  hasDiagrams?: boolean;
  isAccessible?: boolean;
  isShared?: boolean;
  showRequestCancellationCounters?: boolean;
  showIvMedicationCounters?: boolean;
  showTreatmentsCounters?: boolean;
  showOrderCounters?: OrderFormElementType;
  showEKGCounters?: boolean;
  isSaved?: boolean;
  showSignedSections?: boolean;
} & { hasAudits?: boolean; auditSectionCode?: string } & (
    | { hasNotes?: false }
    | ChartNavigationButtonNotesProps
  ) &
  NavigationButtonContentProps;

type AddNewChartButtonProps = {
  encounterId: string;
  defaultIsOpen?: {
    value: boolean;
    chartType: string;
  };
};

const ORDER_ELEMENT_TO_SECTION: Record<OrderFormElementType, string> = {
  Lab: "SLAB",
  Radiology: "SRAD",
  Medication: "SIVM",
  Treatment: "STRE",
  EKG: "SEKG",
  Contrast: "SRAD",
};

const ORDER_BACKED_SECTION_IDS = [
  ...new Set(Object.values(ORDER_ELEMENT_TO_SECTION)),
];

// Chakra button props in this file can exceed TS union limits; keep this cast localized.
const SidebarButton = Button as React.ComponentType<Record<string, unknown>>;

function findChartTypeFromPathname(pathname: string): string {
  const nurseChartCodes = Object.keys(NURSE_CHARTS);
  const providerChartCodes = Object.keys(PROVIDER_CHARTS);

  const isNurse = nurseChartCodes.some((code) => pathname.includes(code));
  if (isNurse) {
    return "Nurse";
  }

  const isProvider = providerChartCodes.some((code) => pathname.includes(code));
  if (isProvider) {
    return "Provider";
  }

  return "";
}

function AddNewChartButton(props: AddNewChartButtonProps) {
  const { encounterId, defaultIsOpen } = props;
  const { isOpen, onClose, onOpen } = useDisclosure({
    defaultIsOpen: defaultIsOpen?.value,
  });

  return (
    <>
      <SidebarButton
        fontSize="0.9375rem"
        color="blue"
        display="flex"
        variant="label"
        alignItems="center"
        marginLeft="7px"
        onClick={onOpen}
      >
        <Icon as={EmrAddRounded} fontSize="1.125rem" marginRight="16px" />
        Add New Chart
      </SidebarButton>
      <AddPatientChartModal
        isOpen={isOpen}
        onClose={onClose}
        encounterId={encounterId}
        defaultChartType={defaultIsOpen?.chartType}
      />
    </>
  );
}

type DiagramsButtonProps = {
  encounterId: string;
  chartCode: string;
};

function DiagramsButton(props: DiagramsButtonProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { chartCode, encounterId } = props;
  const { data, isLoading } = useDiagramsCounter(encounterId, chartCode);

  const diagramsCounter = data?.data || 0;

  return (
    <>
      <Box position="relative">
        <IconButton
          aria-label="notes"
          isLoading={isLoading}
          icon={
            <Box
              width="1.25rem"
              height="1.25rem"
              borderRadius="50%"
              bg={diagramsCounter > 0 ? "blue" : "gray.450"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={EmrPerson}
                color="gray.100"
                width="0.4375rem"
                height="0.5.875rem"
              />
            </Box>
          }
          variant="icon"
          fontSize="1.4375rem"
          minWidth="unset"
          onClick={(e) => {
            e.preventDefault();
            onOpen();
          }}
        />
        {diagramsCounter > 0 && (
          <Counter
            size="sm"
            count={diagramsCounter}
            position="absolute"
            right="-2px"
            top="-2px"
          />
        )}
      </Box>
      <React.Suspense fallback={"Loading..."}>
        <DiagramCanvasModal
          isOpen={isOpen}
          diagrams={[]}
          onClose={onClose}
          {...props}
        />
      </React.Suspense>
    </>
  );
}

type CharNotesButtonProps = {
  onOpen: () => void;
  encounterId: string;
  chartCode: string;
  sectionCode: string;
};

function ChartNotesButton(props: CharNotesButtonProps) {
  const { onOpen, encounterId, chartCode, sectionCode } = props;

  const { data } = useChartSectionNotesCount({
    encounterId,
    chartCode,
    sectionCode,
  });

  const sectionNotesCount = data?.data || 0;

  return (
    <IconButton
      aria-label="notes"
      icon={
        <Icon
          as={EmrNotes}
          color={
            sectionNotesCount && sectionNotesCount > 0 ? "blue" : "gray.450"
          }
          fontSize="1.25rem"
        />
      }
      variant="icon"
      fontSize="1.4375rem"
      minWidth="unset"
      onClick={(e) => {
        e.preventDefault();
        onOpen();
      }}
    />
  );
}

type CountersProps = {
  ordered: number;
  inProcess: number;
  completed: number;
  isLoading: boolean;
};

function Counters({ ordered, inProcess, completed, isLoading }: CountersProps) {
  const boxStyle: ChakraProps = {
    w: "20px",
    h: "20px",
    color: "white",
    borderRadius: "5px",
    textAlign: "center",
    fontSize: "14px",
  };

  return (
    <Flex justifyContent="space-between" gap={1} alignItems="center">
      {isLoading ? (
        <Loading spinnerProps={{ size: "sm", color: "blue" }} />
      ) : (
        <>
          <Box bgColor="orange" {...boxStyle}>
            {ordered}
          </Box>
          <Box bgColor="blue" {...boxStyle}>
            {inProcess}
          </Box>
          <Box bgColor="green" {...boxStyle}>
            {completed}
          </Box>
        </>
      )}
    </Flex>
  );
}

type CompleteAuditActionButtonProps = {
  isAuditTypeComplete: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  canShowUndoIcon: boolean;
  onCompleteClick: () => void;
  onUndoClick: () => void;
};

function CompleteAuditActionButton(props: CompleteAuditActionButtonProps) {
  const {
    isAuditTypeComplete,
    isLoading,
    isDisabled,
    canShowUndoIcon,
    onCompleteClick,
    onUndoClick,
  } = props;

  const completeButtonBg = isAuditTypeComplete ? "green" : "white";
  const completeButtonColor = isAuditTypeComplete ? "white" : "green";

  return (
    <Box w="100%" position="relative">
      <Button
        variant="outlineSquared"
        borderRadius="10px"
        color={completeButtonColor}
        colorScheme="green"
        bgColor={completeButtonBg}
        sx={{
          borderColor: "green",
          height: "45px",
          fontSize: "0.9625rem",
        }}
        w="100%"
        onClick={onCompleteClick}
        isLoading={isLoading}
        isDisabled={isDisabled}
        _disabled={
          isAuditTypeComplete
            ? {
                opacity: 1,
                bg: "green",
                color: "white",
                borderColor: "green",
                cursor: "not-allowed",
              }
            : undefined
        }
        _hover={{
          bg: completeButtonBg,
          color: completeButtonColor,
        }}
        _active={{
          bg: completeButtonBg,
          color: completeButtonColor,
        }}
        _focus={{
          boxShadow: "none",
          bg: completeButtonBg,
          color: completeButtonColor,
        }}
      >
        {isAuditTypeComplete ? "Audit Completed" : "Complete Audit"}
      </Button>

      {canShowUndoIcon && (
        <IconButton
          aria-label="Undo Complete Audit"
          icon={<Icon as={EmrUndo} boxSize="16px" />}
          size="xs"
          variant="unstyled"
          position="absolute"
          top={-2}
          right={-2}
          borderRadius="full"
          bg="white"
          border="1px solid"
          borderColor="green"
          color="green"
          w="28px"
          h="28px"
          minW="28px"
          onClick={onUndoClick}
          isDisabled={isLoading}
        />
      )}
    </Box>
  );
}

function ChartNavigationButton(props: ChartNavigationButtonProps) {
  const {
    label,
    labelIcon,
    sectionId,
    sectionCode,
    hasNotes,
    hasAudits,
    auditSectionCode,
    isShared,
    to,
    chartCode,
    encounterId,
    hasDiagrams,
    showRequestCancellationCounters,
    showIvMedicationCounters,
    showTreatmentsCounters,
    showOrderCounters,
    showEKGCounters,
    isSaved,
    showSignedSections,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isCurrentChartCompleted } = useChartTrackingContext();
  // Commenters (audits access without edit) must still see audit comments even
  // though audited charts are chart-`Completed`, which makes
  // isCurrentChartCompleted true for them.
  const { isCommenter } = useAuditPermissions();
  const { openPanel: openAllyPanel, isAvailable: isAllyAvailable } =
    useHHERAllyContext();

  const location = useLocation();
  const fromAudits = location.pathname.includes("audits");

  const showNotes = hasNotes && fromAudits;
  const showAudits =
    hasAudits && fromAudits && (!isCurrentChartCompleted || isCommenter);
  const showGeneralNotesModal = showNotes && props.notesType === "general";
  const showSharedNotesModal = showNotes && props.notesType === "shared";
  const showOrderNotesModal = showNotes && props.notesType === "order";

  const { data: encounter } = useEncounterSideInfo(encounterId);

  const { data: cancellationCounters, isLoading: cancellationCountersLoading } =
    useEncounterOrderCancellationCounters(encounterId, {
      enabled: showRequestCancellationCounters,
    });

  const { data: ivMedicationCounters, isLoading: ivMedicationCountersLoading } =
    useMedsAndIvCounters(encounterId, {
      enabled: showIvMedicationCounters,
    });

  const { data: treatmentsCounters, isLoading: treatmentsCountersLoading } =
    useTreatmentsCounters(encounterId, {
      enabled: showTreatmentsCounters,
    });

  const { data: orderCounters, isLoading: orderCountersLoading } =
    useEncounterOrderCounters(encounterId, showOrderCounters ?? "Lab", {
      enabled: Boolean(showOrderCounters),
    });

  const { data: ekgCounters, isLoading: ekgCountersLoading } = useEKGCounters(
    encounterId,
    {
      enabled: showEKGCounters,
    }
  );

  const resolvedSectionCode = resolveSectionSuggestionCode(
    sectionId,
    sectionCode
  );
  const { data: sectionSuggestions } = useSectionSuggestions(
    encounterId,
    resolvedSectionCode,
    { enabled: Boolean(resolvedSectionCode) && isAllyAvailable }
  );

  const hasUnreviewedSuggestions =
    isAllyAvailable &&
    (sectionSuggestions?.some((suggestion) => !suggestion.isReviewed) ?? false);

  const handleOpenSectionSuggestions = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    openAllyPanel(resolvedSectionCode);
  };

  const { data: sectionIndicator } = useChartSidebarSectionIndicator({
    encounterId,
    chartCode,
    sectionCode: sectionId,
    enabled: !isShared && !isSaved,
  });

  const { data: sharedSectionIndicator } =
    useChartSidebarSharedSectionIndicator({
      encounterId,
      chartType: chartCode.includes("C") ? "Chart" : "Triage",
      sectionCode: sectionId,
      enabled: !!isShared && !isSaved,
    });

  const { data: orderQueue } = useOrderQueue({
    encounterId,
    enabled:
      ORDER_BACKED_SECTION_IDS.includes(sectionId) &&
      !isSaved &&
      !(isShared
        ? sharedSectionIndicator?.data?.hasDataSaved ?? true
        : sectionIndicator?.data?.hasDataSaved ?? true),
  });

  const queueItems = orderQueue?.data ?? [];

  const hasQueuedForSection = queueItems.some(
    (item: OrderQueueItem) =>
      ORDER_ELEMENT_TO_SECTION[item.elementType] === sectionId
  );

  const counters = {
    ordered:
      (showIvMedicationCounters
        ? ivMedicationCounters?.data.ordered
        : showEKGCounters
        ? ekgCounters?.data.ordered
        : showTreatmentsCounters
        ? treatmentsCounters?.data.ordered
        : orderCounters?.data.ordered) ?? 0,
    inProcess:
      (showIvMedicationCounters
        ? ivMedicationCounters?.data.inProcess
        : showEKGCounters
        ? ekgCounters?.data.inProcess
        : showTreatmentsCounters
        ? treatmentsCounters?.data.inProcess
        : orderCounters?.data.inProcess) ?? 0,
    completed:
      (showIvMedicationCounters
        ? ivMedicationCounters?.data.completed
        : showEKGCounters
        ? ekgCounters?.data.completed
        : showTreatmentsCounters
        ? treatmentsCounters?.data.completed
        : orderCounters?.data.completed) ?? 0,
    administered: showIvMedicationCounters
      ? ivMedicationCounters?.data.administered
      : 0,
  };

  const hasActiveCounters =
    counters.ordered +
      counters.inProcess +
      counters.completed +
      (counters.administered ?? 0) >
    0;

  return (
    <>
      <NavigationButton to={to} key={to}>
        <NavigationButtonContent
          label={label}
          labelIcon={
            showAudits ? (
              <Box mr="10px">
                {isShared ? (
                  <AuditCommentsSharedSectionIndicator
                    sectionCode={auditSectionCode ?? sectionId}
                    encounterId={encounterId}
                    isFromSideBar={true}
                  />
                ) : (
                  <AuditCommentsSectionIndicator
                    chartCode={chartCode}
                    sectionCode={auditSectionCode ?? sectionId}
                    encounterId={encounterId}
                    isFromSideBar={true}
                  />
                )}
              </Box>
            ) : !fromAudits ? (
              labelIcon
            ) : (
              <Box mr="35px" />
            )
          }
          isSaved={
            isSaved ||
            Boolean(sectionIndicator?.data.hasDataSaved) ||
            Boolean(sharedSectionIndicator?.data.hasDataSaved) ||
            hasQueuedForSection ||
            hasActiveCounters
          }
        />
        {showIvMedicationCounters && !ivMedicationCountersLoading && (
          <Box
            bgColor="purple"
            sx={{
              w: "20px",
              h: "20px",
              color: "white",
              borderRadius: "5px",
              textAlign: "center",
              fontSize: "14px",
            }}
          >
            {counters.administered ?? 0}
          </Box>
        )}
        <HStack spacing="14px">
          {label === "Vitals" && <VitalsTimerBadge encounterId={encounterId} />}
          {hasDiagrams && !fromAudits && (
            <DiagramsButton encounterId={encounterId} chartCode={chartCode} />
          )}

          {hasNotes && showNotes && (
            <ChartNotesButton
              onOpen={onOpen}
              encounterId={encounterId}
              chartCode={chartCode}
              sectionCode={sectionId}
            />
          )}

          {(showIvMedicationCounters ||
            Boolean(showOrderCounters) ||
            showEKGCounters ||
            showTreatmentsCounters) && (
            <Counters
              ordered={counters.ordered}
              inProcess={counters.inProcess}
              completed={counters.completed}
              isLoading={
                ivMedicationCountersLoading ||
                orderCountersLoading ||
                ekgCountersLoading ||
                treatmentsCountersLoading
              }
            />
          )}
          {hasUnreviewedSuggestions && (
            <Box
              role="button"
              aria-label="Open section suggestions"
              bg="#FF9500"
              w="20px"
              h="20px"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              cursor="pointer"
              tabIndex={0}
              onClick={handleOpenSectionSuggestions}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  openAllyPanel(resolvedSectionCode);
                }
              }}
            >
              <Icon as={EmrEar} color="white" w="8px" h="11px" />
            </Box>
          )}
          {showRequestCancellationCounters &&
            (cancellationCounters?.data?.requestCancellation ?? 0) > 0 &&
            !cancellationCountersLoading && (
              <Icon as={EmrBubbleChatIndicator} color="red" fontSize="22px" />
            )}

          {showSignedSections && (
            <Box display="flex" gap={3}>
              {encounter?.data.isObservation && (
                <SignedSectionButton
                  encounterId={encounterId}
                  type="Observation"
                  icon={<Icon as={EmrBed} fontSize="24px" />}
                />
              )}

              <SignedSectionButton
                encounterId={encounterId}
                type="Discharge"
                icon={<Icon as={EmrDischarged} fontSize="24px" />}
              />

              <SignedSectionButton
                encounterId={encounterId}
                type="Transfer"
                icon={<Icon as={EmrCar} fontSize="28px" ml="6px" />}
              />
            </Box>
          )}
        </HStack>
      </NavigationButton>
      {showGeneralNotesModal && (
        <ChartNotesModal
          chartId={chartCode}
          isOpen={isOpen}
          encounterId={encounterId}
          onClose={onClose}
          sectionId={sectionId}
          title={label}
        />
      )}
      {showSharedNotesModal && (
        <SharedChartNotesModal
          isOpen={isOpen}
          encounterId={encounterId}
          onClose={onClose}
          sectionCode={sectionId}
          title={label}
        />
      )}
      {showOrderNotesModal && (
        <OrderChartNotesModal
          isOpen={isOpen}
          onClose={onClose}
          encounterId={encounterId}
          chartCode={chartCode || ""}
          orderType={props.orderType}
          title={label}
        />
      )}
    </>
  );
}

function MoveToBillingButton() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { scope } = usePermissions();
  const queryClient = useQueryClient();

  const chartType = findChartTypeFromPathname(location.pathname);
  const commenterType = chartType == "Nurse" ? "Nurse" : "Provider";
  const { isAuditor, isCommenter } = useAuditPermissions();

  const { encounterId = "" } = useParams<{ encounterId: string }>();

  const { pathname } = useLocation();

  const type = findChartTypeFromPathname(pathname);

  const [confirmMessage, setConfirmMessage] = React.useState("");

  const onUpdateAudit = async (status: string) => {
    try {
      await updateAuditStatus({
        status: status,
        type: commenterType,
      });
      onConfirmationOpen();
      onProviderReviewWarningClose();
      onAuditorReviewWarningClose();
      onBillingWarningClose();
      toast({
        description: formMessages.updateSuccess("Audit status updated"),
      });
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  const { data: audit, isLoading: isLoadingAudit } =
    useAuditStatus(encounterId);

  const auditStatuses = React.useMemo(
    () => [
      audit?.data?.demographicAuditStatus,
      audit?.data?.providerChartAuditStatus,
      audit?.data?.nurseChartAuditStatus,
    ],
    [audit]
  );

  const isAnyAuditIncomplete = auditStatuses.some(
    (status) => status !== "Completed"
  );

  const {
    mutateAsync: updateAuditStatus,
    isLoading: isUpdateAuditStatusLoading,
  } = useUpdateAuditStatus(encounterId);

  const {
    isOpen: isBillingWarningOpen,
    onClose: onBillingWarningClose,
    onOpen: onBillingWarningOpen,
  } = useDisclosure();

  const {
    isOpen: isUndoCompleteWarningOpen,
    onClose: onUndoCompleteWarningClose,
    onOpen: onUndoCompleteWarningOpen,
  } = useDisclosure();

  const {
    isOpen: isProviderReviewWarningOpen,
    onClose: onProviderReviewWarningClose,
    onOpen: onProviderReviewWarningOpen,
  } = useDisclosure();

  const {
    isOpen: isAuditorReviewWarningOpen,
    onClose: onAuditorReviewWarningClose,
    onOpen: onAuditorReviewWarningOpen,
  } = useDisclosure();

  const {
    isOpen: isConfirmationOpen,
    onClose: onConfirmationClose,
    onOpen: onConfirmationOpen,
  } = useDisclosure();

  const onCompleteAudit = async () => {
    try {
      await updateAuditStatus({
        status: "Completed",
        type,
      });
      onBillingWarningClose();
      toast({
        description: "Your Audit Review has been marked as complete.",
      });
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  const onUndoCompleteAudit = async () => {
    try {
      if (!isAuditTypeComplete || !isAnyAuditIncomplete) {
        toast({
          description: "Audit is not completed or all audits are complete.",
        });
        return;
      }

      await updateAuditStatus({
        status: "InReview",
        type,
      });
      onUndoCompleteWarningClose();
      toast({
        description: "Your Audit Review has been reopened.",
      });
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  const isAuditComplete =
    audit?.data?.demographicAuditStatus === "Completed" &&
    audit?.data?.providerChartAuditStatus === "Completed" &&
    audit?.data?.nurseChartAuditStatus === "Completed";

  const isAuditTypeComplete =
    (type === "Nurse" && audit?.data?.nurseChartAuditStatus === "Completed") ||
    (type === "Provider" &&
      audit?.data?.providerChartAuditStatus === "Completed");

  const hasUndoCompletePermission = scope("audits:undocomplete").isAccessible;

  const canUndoCompleteAudit =
    isAuditTypeComplete && isAnyAuditIncomplete && hasUndoCompletePermission;

  const isReviewDisabled =
    isUpdateAuditStatusLoading ||
    isLoadingAudit ||
    isAuditComplete ||
    isAuditTypeComplete;

  const isCompleteButtonDisabled =
    isAuditTypeComplete ||
    isCommenter ||
    isLoadingAudit ||
    isUpdateAuditStatusLoading;

  const getUnreviewedCommentsWarning = () => {
    type SectionIndicatorParams = {
      chartCode: string;
      sectionCode: string;
      encounterId: string;
      validateSubsections?: boolean;
    };

    type SharedSectionIndicatorParams = {
      sectionCode: string;
      encounterId: string;
      validateSubsections?: boolean;
    };

    const isSectionIndicatorKey = (
      key: QueryKey
    ): key is ["AuditCommentsSectionIndicator", SectionIndicatorParams] =>
      Array.isArray(key) &&
      key.length === 2 &&
      key[0] === "AuditCommentsSectionIndicator" &&
      typeof key[1] === "object" &&
      key[1] !== null &&
      "chartCode" in key[1] &&
      "sectionCode" in key[1] &&
      "encounterId" in key[1];

    const isSharedSectionIndicatorKey = (
      key: QueryKey
    ): key is [
      "AuditCommentsSharedSectionIndicator",
      SharedSectionIndicatorParams
    ] =>
      Array.isArray(key) &&
      key.length === 2 &&
      key[0] === "AuditCommentsSharedSectionIndicator" &&
      typeof key[1] === "object" &&
      key[1] !== null &&
      "sectionCode" in key[1] &&
      "encounterId" in key[1];

    const currentChartCode =
      pathname.match(/\/(?:triage|chart)\/([^/]+)/i)?.[1] ?? "";

    const sectionQueries = queryClient.getQueriesData<
      AxiosResponse<AuditCommentsIndicator>
    >(["AuditCommentsSectionIndicator"]);
    const sharedSectionQueries = queryClient.getQueriesData<
      AxiosResponse<AuditCommentsIndicator>
    >(["AuditCommentsSharedSectionIndicator"]);

    const unreviewedSectionKeys = new Set<string>();

    [...sectionQueries, ...sharedSectionQueries].forEach(([key, response]) => {
      if (!response?.data?.hasComments || response?.data?.hasReview) {
        return;
      }

      if (isSectionIndicatorKey(key)) {
        const params = key[1];
        if (
          params.encounterId !== encounterId ||
          params.chartCode !== currentChartCode
        ) {
          return;
        }
        unreviewedSectionKeys.add(`${params.chartCode}:${params.sectionCode}`);
      } else if (isSharedSectionIndicatorKey(key)) {
        const params = key[1];
        if (params.encounterId !== encounterId) {
          return;
        }
        unreviewedSectionKeys.add(`shared:${params.sectionCode}`);
      }
    });

    if (unreviewedSectionKeys.size === 0) return null;

    return (
      <>
        <br />
        <Text as="span" color="red">
          Warning: {unreviewedSectionKeys.size} section(s) with comments have
          not been marked as reviewed (based on loaded sections).
        </Text>
      </>
    );
  };

  return (
    <>
      <Box>
        <VStack>
          <CompleteAuditActionButton
            isAuditTypeComplete={isAuditTypeComplete}
            isLoading={isUpdateAuditStatusLoading || isLoadingAudit}
            isDisabled={isCompleteButtonDisabled}
            canShowUndoIcon={canUndoCompleteAudit}
            onCompleteClick={onBillingWarningOpen}
            onUndoClick={onUndoCompleteWarningOpen}
          />

          {isCommenter && (
            <>
              <WarningDialog
                isOpen={isAuditorReviewWarningOpen}
                onCancel={onAuditorReviewWarningClose}
                title="Warning!"
                mainText={
                  <chakra.span>
                    Are you sure you want to move this to the Auditor for
                    Approval?
                    {getUnreviewedCommentsWarning()}
                  </chakra.span>
                }
                onClose={onAuditorReviewWarningClose}
                onAction={() => {
                  setConfirmMessage(
                    "Your Audit Review has been sent back to the Auditor for Approval."
                  );
                  onUpdateAudit("AuditorReview");
                }}
                cancelLabel="Cancel"
                actionLabel="Move to Auditor"
                blockScrollOnMount={false}
                cancelButtonProps={{ color: "red" }}
                actionButtonProps={{ color: "blue" }}
                actionsDirection="vertical"
              />
              <Button
                variant="outlineSquared"
                borderRadius="10px"
                w="100%"
                sx={{ height: "45px", fontSize: "0.9625rem" }}
                onClick={onAuditorReviewWarningOpen}
                isLoading={isUpdateAuditStatusLoading || isLoadingAudit}
                isDisabled={isReviewDisabled}
              >
                Move for Auditor Review
              </Button>
            </>
          )}
          {isAuditor && (
            <>
              <WarningDialog
                isOpen={isProviderReviewWarningOpen}
                onCancel={onProviderReviewWarningClose}
                title="Warning!"
                mainText={`Are you sure you want to move this to the ${commenterType} for Review?`}
                onClose={onProviderReviewWarningClose}
                onAction={() => {
                  setConfirmMessage(
                    `Your Audit Review has been sent to the ${commenterType} for Review.`
                  );
                  onUpdateAudit("InReview");
                }}
                cancelLabel="Cancel"
                actionLabel={`Move to ${commenterType}`}
                blockScrollOnMount={false}
                cancelButtonProps={{ color: "red" }}
                actionButtonProps={{ color: "blue" }}
                actionsDirection="vertical"
              />
              <Button
                variant="outlineSquared"
                borderRadius="10px"
                w="100%"
                sx={{ height: "45px", fontSize: "0.9625rem" }}
                onClick={onProviderReviewWarningOpen}
                borderColor={
                  commenterType === "Nurse" ? "red-sizzling" : "blue"
                }
                color={commenterType === "Nurse" ? "red-sizzling" : "blue"}
                isLoading={isUpdateAuditStatusLoading || isLoadingAudit}
                isDisabled={isReviewDisabled}
              >
                Move for {commenterType} Review
              </Button>

              <WarningDialog
                isOpen={isBillingWarningOpen}
                onCancel={onBillingWarningClose}
                title="Warning!"
                mainText={
                  <chakra.span>
                    Are you sure you want to move this to billing?
                    <br />
                    Moving this to Billing means you will complete this Audit.
                    You will not be able to edit this chart again.
                  </chakra.span>
                }
                onClose={onBillingWarningClose}
                onAction={() => {
                  setConfirmMessage(
                    `Your Audit Review has been marked as complete.`
                  );
                  onUpdateAudit("Completed");
                }}
                cancelLabel="Cancel"
                actionLabel="Complete Audit"
                blockScrollOnMount={false}
                cancelButtonProps={{ color: "red" }}
                actionButtonProps={{ color: "blue" }}
                actionsDirection="vertical"
              />
            </>
          )}
        </VStack>
      </Box>

      <WarningDialog
        isOpen={isUndoCompleteWarningOpen}
        onCancel={onUndoCompleteWarningClose}
        title="Warning!"
        mainText="Are you sure you want to Undo Complete Audit?"
        onClose={onUndoCompleteWarningClose}
        onAction={onUndoCompleteAudit}
        cancelLabel="Cancel"
        actionLabel="Undo Complete Audit"
        blockScrollOnMount={false}
        cancelButtonProps={{ color: "red" }}
        actionButtonProps={{ color: "blue" }}
        actionsDirection="vertical"
        isActionLoading={isUpdateAuditStatusLoading || isLoadingAudit}
      />

      <WarningDialog
        isOpen={isBillingWarningOpen}
        onCancel={onBillingWarningClose}
        title="Warning!"
        mainText={
          <chakra.span>
            Are you sure you want to move this to billing?
            <br />
            Moving this to Billing means you will complete this Audit. You will
            not be able to edit this chart again.
          </chakra.span>
        }
        onClose={onBillingWarningClose}
        onAction={onCompleteAudit}
        cancelLabel="Cancel"
        actionLabel="Complete Audit"
        blockScrollOnMount={false}
        cancelButtonProps={{ color: "red" }}
        actionButtonProps={{ color: "blue" }}
        actionsDirection="vertical"
        isActionLoading={isUpdateAuditStatusLoading || isLoadingAudit}
      />

      <WarningDialog
        isOpen={isConfirmationOpen}
        onCancel={onConfirmationClose}
        title="Sent"
        mainText={confirmMessage}
        onClose={onConfirmationClose}
        onAction={() => navigate("/audits/incomplete")}
        actionLabel="Ok"
        blockScrollOnMount={false}
        actionButtonProps={{ color: "blue" }}
        actionsDirection="vertical"
        onlyActionButton
      />
    </>
  );
}

function ChartsSidebar(props: ChartsSidebarProps) {
  const { children } = props;
  const { encounterId = "" } = useParams<{ encounterId: string }>();
  const { user, isLoading: isAuthLoading } = useAuth0();
  const { isPastEncounter } = useChartTrackingContext();
  const navigate = useNavigate();
  const { scope } = usePermissions();
  const { setAuditChartReadOnly } = useAuditChartReadOnly();
  const location = useLocation();
  // Ungated role check on purpose — see useAuditPermissions. This feeds
  // isNotAssignedAuditChart, which decides the lock published below; the
  // lock-gated isAuditor would make that decision depend on its own result.
  const { hasAuditorRole: isAuditor } = useAuditPermissions();
  const currentUserId = (user?.[USERID_CLAIMS] as string) || "";

  const createChart = new URLSearchParams(location.search).get("createChart");

  const [isEditMode, setIsEditMode] = React.useState(!!createChart);
  const [hasActiveCharts, setHasActiveCharts] = React.useState(false);
  const { ref, scrollbarWidth } = useScrollbarWidth<HTMLDivElement>();
  const hasInitializedRoute = React.useRef(false);
  const [fromRoute, setFromRoute] = React.useState<string>("");
  const [storedFromLabel, setStoredFromLabel] = React.useState<string>("");
  const [nurseChartAuditorId, setNurseChartAuditorId] =
    React.useState<string>("");
  const [providerChartAuditorId, setProviderChartAuditorId] =
    React.useState<string>("");
  const {
    isOpen: isNotAssignedWarningOpen,
    onOpen: onNotAssignedWarningOpen,
    onClose: onNotAssignedWarningClose,
  } = useDisclosure();

  const contentPadding = `12px ${24 - scrollbarWidth}px 8px 24px`;

  const { fromRoute: fromRouteParams, fromLabel: defaultFromLabel } =
    determineChartRouteAndLabel(location.pathname);

  const locationState =
    (location.state as {
      fromRoute?: string;
      fromLabel?: string;
      isPastEncounter: boolean;
      nurseChartAuditorId?: string;
      providerChartAuditorId?: string;
    }) || null;

  const [searchParams, setSearchParams] = useSearchParams();
  const searchState = React.useMemo(
    () => ({
      fromRoute: searchParams.get("fromRoute"),
      fromLabel: searchParams.get("fromLabel"),
      isPastEncounter: searchParams.get("isPastEncounter") === "true",
      nurseChartAuditorId: searchParams.get("nurseChartAuditorId"),
      providerChartAuditorId: searchParams.get("providerChartAuditorId"),
    }),
    [searchParams]
  );

  const routeHints = React.useMemo(
    () => [
      location.hash,
      location.pathname,
      fromRoute,
      fromRouteParams,
      locationState?.fromRoute,
      searchState.fromRoute,
      storedFromLabel,
      locationState?.fromLabel,
      searchState.fromLabel,
    ],
    [
      fromRoute,
      fromRouteParams,
      location.hash,
      location.pathname,
      locationState?.fromLabel,
      locationState?.fromRoute,
      searchState.fromLabel,
      searchState.fromRoute,
      storedFromLabel,
    ]
  );

  const isAuditsView = React.useMemo(
    () =>
      routeHints.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes("audits")
      ),
    [routeHints]
  );

  const inferredAuditsRoute = React.useMemo(() => {
    const hasCompletedAuditsRoute = routeHints.some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes("audits/completed")
    );

    return hasCompletedAuditsRoute ? "/audits/completed" : "/audits/incomplete";
  }, [routeHints]);

  const resolvedFromRoute =
    fromRoute ||
    searchState.fromRoute ||
    locationState?.fromRoute ||
    fromRouteParams ||
    (isAuditsView ? inferredAuditsRoute : "");

  const resolvedBackLabel =
    (isAuditsView ? "Audits" : storedFromLabel) || "Back";

  React.useEffect(() => {
    if (!hasInitializedRoute.current) {
      if (
        (!searchState.fromRoute || !searchState.fromLabel) &&
        locationState?.fromRoute &&
        locationState?.fromLabel
      ) {
        setSearchParams({
          fromRoute: locationState.fromRoute,
          fromLabel: locationState.fromLabel,
          isPastEncounter: String(locationState.isPastEncounter ?? false),
          nurseChartAuditorId: locationState.nurseChartAuditorId || "",
          providerChartAuditorId: locationState.providerChartAuditorId || "",
        });
      }

      if (locationState?.fromRoute) {
        setFromRoute(locationState.fromRoute);
      } else if (searchState.fromRoute) {
        setFromRoute(searchState.fromRoute);
      } else {
        setFromRoute(fromRouteParams);
      }

      if (locationState?.fromLabel) {
        setStoredFromLabel(locationState.fromLabel);
      } else if (searchState?.fromLabel) {
        setStoredFromLabel(searchState.fromLabel);
      } else {
        setStoredFromLabel(defaultFromLabel);
      }

      if (locationState?.fromLabel !== undefined) {
        setStoredFromLabel(locationState.fromLabel);
      } else if (searchState?.fromLabel !== undefined) {
        setStoredFromLabel(searchState.fromLabel || "Back");
      }

      if (locationState?.nurseChartAuditorId !== undefined) {
        setNurseChartAuditorId(locationState.nurseChartAuditorId);
      } else if (searchState?.nurseChartAuditorId !== undefined) {
        setNurseChartAuditorId(searchState.nurseChartAuditorId || "");
      }

      if (locationState?.providerChartAuditorId !== undefined) {
        setProviderChartAuditorId(locationState.providerChartAuditorId);
      } else if (searchState?.providerChartAuditorId !== undefined) {
        setProviderChartAuditorId(searchState.providerChartAuditorId || "");
      }

      hasInitializedRoute.current = true;
    }
  }, [
    fromRouteParams,
    fromRoute,
    searchState,
    locationState,
    defaultFromLabel,
    setSearchParams,
  ]);

  const currentAuditChartType = React.useMemo(() => {
    // Detect the chart-type segment case-insensitively. The audits list
    // navigates with lowercase `/triage/` `/chart/` routes, but moving between a
    // patient's charts via the sidebar ChartPicker produces capitalized
    // `/Triage/` `/Chart/` segments. A case-sensitive check loses the chart type
    // on picker navigation, so the audit is not revalidated for the new chart.
    const chartTypeSegment = location.pathname
      .match(/\/(triage|chart)\/[^/]+/i)?.[1]
      ?.toLowerCase();
    if (chartTypeSegment === "triage") return "Nurse";
    if (chartTypeSegment === "chart") return "Provider";
    return "";
  }, [location.pathname]);

  // The audits list seeds the assigned auditors through navigation state, but
  // ChartPicker moves between a patient's charts with a bare path — no query
  // string and no router state — so that seed is lost on the first chart switch
  // that remounts this sidebar. Prefer the audit-status query, which is keyed on
  // the encounter and survives any navigation, and keep the navigation state as
  // a fallback for the window before the query resolves.
  const { data: auditStatus } = useAuditStatus(encounterId, {
    enabled: isAuditsView && !!encounterId,
  });

  const assignedAuditorForCurrentChart = React.useMemo(() => {
    if (currentAuditChartType === "Nurse")
      return auditStatus?.data?.nurseChartAuditorId || nurseChartAuditorId;
    if (currentAuditChartType === "Provider")
      return (
        auditStatus?.data?.providerChartAuditorId || providerChartAuditorId
      );
    return "";
  }, [
    auditStatus,
    currentAuditChartType,
    nurseChartAuditorId,
    providerChartAuditorId,
  ]);

  const isAuditChartFromAudits = storedFromLabel === "Audits";
  const hasResolvedCurrentUserId = !isAuthLoading && !!currentUserId;
  const hasAssignedAuditorForCurrentChart = !!assignedAuditorForCurrentChart;
  const isNotAssignedAuditChart =
    isAuditChartFromAudits &&
    isAuditor &&
    !!currentAuditChartType &&
    hasResolvedCurrentUserId &&
    hasAssignedAuditorForCurrentChart &&
    !isSameUserId(assignedAuditorForCurrentChart, currentUserId);

  const isUnassignedAuditChart =
    isAuditChartFromAudits &&
    isAuditor &&
    !!currentAuditChartType &&
    !hasAssignedAuditorForCurrentChart;

  const isAuditTypeComplete =
    getAuditStatusForChartType(auditStatus?.data, currentAuditChartType) ===
    "Completed";

  const isReadOnlyAuditChart =
    isNotAssignedAuditChart || isUnassignedAuditChart || isAuditTypeComplete;

  React.useEffect(() => {
    if (isNotAssignedAuditChart) {
      onNotAssignedWarningOpen();
    }
  }, [isNotAssignedAuditChart, onNotAssignedWarningOpen]);

  // useLayoutEffect, not useEffect: this publishes the lock gating the chart
  // surfaces alongside this sidebar, clearing it to `null` (never `false`)
  // outside the audits flow. After paint an auditor would see it interactive.
  React.useLayoutEffect(() => {
    setAuditChartReadOnly(isAuditChartFromAudits ? isReadOnlyAuditChart : null);

    return () => {
      setAuditChartReadOnly(null);
    };
  }, [isAuditChartFromAudits, isReadOnlyAuditChart, setAuditChartReadOnly]);

  const handleNotAssignedWarningClose = React.useCallback(() => {
    onNotAssignedWarningClose();
  }, [onNotAssignedWarningClose]);

  const handleBack = () => {
    const normalizedFromRoute = String(resolvedFromRoute ?? "").trim();
    if (!normalizedFromRoute) return;

    setFromRoute("");
    navigate(
      normalizedFromRoute.startsWith("/")
        ? normalizedFromRoute
        : `/${normalizedFromRoute}`,
      {
        replace: true,
      }
    );
  };

  const addPermission =
    scope("nurse:add").isAccessible || scope("provider:add").isAccessible;

  return (
    <Sidebar>
      <SidebarActions>
        {!isPastEncounter && (
          <BackButton onClick={handleBack}>{resolvedBackLabel}</BackButton>
        )}
        <ChartSectionHeaderMenu encounterId={encounterId} />
      </SidebarActions>
      <SidebarHeader
        padding="0 17px"
        gap={2}
        display="flex"
        flexDirection="column"
      >
        {isAuditsView && <MoveToBillingButton />}
        <SidebarChartPatientCard patientId={encounterId} marginBottom="10px" />
      </SidebarHeader>
      <SidebarContent ref={ref} padding={contentPadding} overflow="hidden">
        <Flex
          direction="column"
          height="100%"
          sx={{
            overflowX: "hidden",
          }}
        >
          <ChartPicker
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            setHasActiveCharts={setHasActiveCharts}
          />
          <Divider
            margin="10px -7px 10px -7px"
            borderColor="gray.500"
            width="unset"
          />
          {isEditMode && addPermission ? (
            <AddNewChartButton
              encounterId={encounterId}
              defaultIsOpen={{
                value: !!createChart || !hasActiveCharts,
                chartType: createChart || "demographics",
              }}
            />
          ) : (
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: "auto",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {children}
            </Box>
          )}
        </Flex>
      </SidebarContent>
      <SidebarBottom>
        <SidebarUserView showFaqButton />
      </SidebarBottom>

      <WarningDialog
        isOpen={isNotAssignedWarningOpen}
        onCancel={handleNotAssignedWarningClose}
        title="Warning"
        mainText="You are not assigned to Audit this encounter."
        onClose={handleNotAssignedWarningClose}
        onAction={handleNotAssignedWarningClose}
        actionLabel="Ok"
        blockScrollOnMount={false}
        actionButtonProps={{ color: "blue" }}
        actionsDirection="vertical"
        onlyActionButton
      />
    </Sidebar>
  );
}

export type { ChartsSidebarProps, ChartNavigationButtonProps };
export { ChartsSidebar, ChartNavigationButton };
